import * as Types from '@aeppic/types'
import { MapIndex } from './map-index'
import { BTreeIndex } from './btree-index'
import { DocumentIdIterator } from './index-interface'

/*
 * TS believes BTreeModule has a default export (which is true) and correctly
 * want to import that. NodeJS though (when just running through a TS remover
 * as ts-node (transpile) via ava does it), imports the module.exports
 * thus the types don't match. Recasting here via explicit default property
 * access to make it match
 */
import BTreeModule from 'sorted-btree'
const BTree: typeof BTreeModule = (BTreeModule as any).default ?? BTreeModule


export interface IndexInclusionFilter {
  (entry: Types.Document): boolean
}

export interface GeneralIndexOptions {
  inclusionFilter?: IndexInclusionFilter
  useBTree?: boolean // Added option to use BTree
}

export interface PrefixTextIndexOptions extends GeneralIndexOptions { // Ensure it extends GeneralIndexOptions
  type: 'prefix'
  length: number
}

export type IndexOptions = GeneralIndexOptions | PrefixTextIndexOptions // Simplified, as PrefixTextIndexOptions now includes GeneralIndexOptions fields

export type IndexMap = { [fieldPath: string]: Index };

// Utility type guard for BTree
function isBTree<K, V1, V2>(mapOrBTree: Map<K, V1> | BTreeModule<K, V2>): mapOrBTree is BTreeModule<K, V2> {
  return mapOrBTree instanceof BTree;
}

export class Index {
  private _impl: MapIndex | BTreeIndex

  constructor(private _fieldPath: string, private _options: IndexOptions = {}) {
    if (this._options.useBTree) {
      this._impl = new BTreeIndex(_fieldPath, _options)
    } else {
      this._impl = new MapIndex(_fieldPath, _options)
    }
  }

  get isBTree() {
    return this._options.useBTree
  }

  get fieldPath() {
    return this._fieldPath
  }

  get prefixLength() {
    return this._impl.prefixLength
  }

  get isSuperSet() {
    return this._impl.isSuperSet
  }

  public size() {
    return this._impl.size()
  }

  public entryCount() { 
    return this._impl.entryCount()
  }

  public addIfMatches(entry: Types.Document) {
    this._impl.addIfMatches(entry)
  }

  has(id: string) {
    return this._impl.has(id)
  }

  public remove(id: string) {
    if (typeof id !== 'string') {
      throw new Error('Document id must be a string')
    }
    this._impl.remove(id)
  }

  public getDocumentIdsForValue(value: any): DocumentIdIterator {
    return this._impl.getDocumentIdsForValue(value)
  }

  public *enumerateMatches(minValue: any, maxValue: any = minValue): IterableIterator<Types.DocumentId> {
    const e = this.getEntriesForRange(minValue, maxValue)

    for (const [_, entries] of e) {
      for (const entryId of entries) {
        yield entryId
      }
    }
  }

  public getEntriesForRange(minValue: any, maxValue: any): Array<[key: any, entries: DocumentIdIterator]> {
    return this._impl.getEntriesForRange(minValue, maxValue)
  }

  public mappedValues(): IterableIterator<any> {
    return this._impl.mappedValues()
  }
}

// Utility function to resolve a field in an object
//
// It will accept a path like 'a.b.c' and return the value of object.a.b.c

// If the value is an array, it will return an array of values for each element
// It does not support deeper arrays. 
//
// E.g. given the object:
//
// ```json
// {
//   a: 'A',
//   data: {
//     b: 'B',
//     c: 4,
//     d: {
//       id: 'E',
//       text: 'F',
//     },
//     e: [
//       { id: 'G', text: 'H' },
//       { id: 'I', text: 'J' },
//     ],
//    },
//  }
// ```
//
// Then the following calls will return:
// 
// resolveField(object, 'a') => 'A'
// resolveField(object, 'data.b') => 'B'
// resolveField(object, 'data.c') => 4
// resolveField(object, 'data.d.id') => 'E'
// resolveField(object, 'data.d.text') => 'F'
// resolveField(object, 'data.e.id') => ['G', 'I']
// 
// This is specific to the way aeppic documents and their data fields are structured
// 
export function resolveField(object: object, path: string) {
  const parts = path.split('.')
  let value: any = object

  for (const part of parts) {
    if (value == null) {
      return undefined
    }

    if (Array.isArray(value)) {
      return value.map(v => v[part])
    } else {
      value = value[part]
    }
  }

  if (value === object) {
    value = undefined
  }

  return value
}

export function intersectSets(sets: Set<string>[]): string[] {
  const sortedSets = new Array<Set<string>>(sets.length)
  
  for (let i = 0; i < sets.length; i ++) {
    const set = sets[i]

    if (!set || set.size === 0) {
      return []
    }

    sortedSets[i] = set
  }
  
  if (sortedSets.length === 0) {
    return []
  }

  sortedSets.sort((a, b) => a.size - b.size)
  
  const intersectionEnumerator = intersect(sortedSets)
  return Array.from(intersectionEnumerator)
}

function intersect<T>(sets: Set<T>[]): Set<T> {
  if (sets.length === 0) {
    return 
  } else if (sets.length === 1) {
    return sets[0]
  } else {
    const set = sets.shift()
    const intersected = new Set<T>()

    for (const key of set) {
      let isInLowerSets = true

      for (const set of sets) {
        if (!set.has(key)) {
          isInLowerSets = false
          break
        }
      }

      if (isInLowerSets) {
        intersected.add(key)
      }
    }

    return intersected
  }
}
